if (!customElements.get('media-video')) {
  customElements.define(
    'media-video',
    // Plays muted while in view; the product media modal relies on this too, its hidden items are never in view
    class MediaVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.playButton = this.querySelector('[data-media-play]');
        this.pauseButton = this.querySelector('[data-media-pause]');
        this.soundButton = this.querySelector('[data-media-sound]');
        this.captionsButton = this.querySelector('[data-media-captions]');
        this.cue = this.querySelector('[data-media-cue]');
        this.track = this.video.querySelector('track')?.track;
        this.autoplay = this.hasAttribute('data-autoplay');
        this.held = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.captionsOn = this.hasAttribute('data-captions-default');
        this.video.muted = true;

        this.video.addEventListener('play', () => this.update());
        this.video.addEventListener('pause', () => this.update());
        this.video.addEventListener('volumechange', () => this.update());
        this.video.addEventListener('waiting', () => this.classList.add('is-loading'));
        this.video.addEventListener('playing', () => this.classList.remove('is-loading'));
        this.video.addEventListener('error', () => this.fail());
        this.video.querySelector('source:last-of-type')?.addEventListener('error', () => this.fail());
        this.playButton?.addEventListener('click', () => this.start());
        this.pauseButton?.addEventListener('click', () => this.togglePlay());
        this.soundButton?.addEventListener('click', () => this.toggleSound());
        this.captionsButton?.addEventListener('click', () => this.toggleCaptions());

        if (this.track && this.cue) {
          this.track.mode = 'hidden';
          this.track.addEventListener('cuechange', () => this.renderCue());
        }
        if (this.playButton && (!this.autoplay || this.held)) this.classList.add('is-poster');

        this.observer = new IntersectionObserver(([entry]) => this.onView(entry.isIntersecting), { threshold: 0.25 });
        this.observer.observe(this);
        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      onView(visible) {
        if (!visible) {
          this.resumeInView = !this.video.paused;
          this.video.pause();
        } else if ((this.autoplay || this.resumeInView) && !this.classList.contains('is-poster')) {
          this.play();
        }
      }

      play() {
        if (!this.held) this.video.play().catch(() => this.update());
      }

      start() {
        this.classList.remove('is-poster');
        this.held = false;
        if (this.dataset.sound === 'on_play') this.video.muted = false;
        this.video.play().catch(() => {
          this.video.muted = true;
          this.video.play().catch(() => this.update());
        });
        (this.pauseButton ?? this.video).focus();
      }

      togglePlay() {
        this.held = !this.video.paused;
        if (this.held) this.video.pause();
        else this.video.play().catch(() => this.update());
      }

      // Browsers pause a muted autoplay video when it is unmuted without user activation
      toggleSound() {
        this.video.muted = !this.video.muted;
        if (!this.video.muted && !this.held) {
          this.video.play().catch(() => {
            this.video.muted = true;
            this.video.play().catch(() => this.update());
          });
        }
        this.update();
      }

      toggleCaptions() {
        this.captionsOn = !this.captionsOn;
        this.renderCue();
        this.update();
      }

      renderCue() {
        const text = this.captionsOn ? [...(this.track.activeCues ?? [])].map((cue) => cue.text).join('\n') : '';
        this.cue.textContent = text;
        this.cue.hidden = !text;
      }

      fail() {
        this.classList.remove('is-poster', 'is-loading');
        this.classList.add('is-unavailable');
      }

      update() {
        const paused = this.video.paused;
        this.classList.toggle('is-paused', paused);
        this.pauseButton?.setAttribute(
          'aria-label',
          paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
        );
        if (this.soundButton) {
          const soundOn = !this.video.muted;
          this.classList.toggle('is-sound-on', soundOn);
          this.soundButton.setAttribute('aria-pressed', String(soundOn));
          this.soundButton.setAttribute(
            'aria-label',
            soundOn ? this.soundButton.dataset.labelOff : this.soundButton.dataset.labelOn
          );
        }
        if (this.captionsButton) {
          this.classList.toggle('is-captions-on', this.captionsOn);
          this.captionsButton.setAttribute('aria-pressed', String(this.captionsOn));
          this.captionsButton.setAttribute(
            'aria-label',
            this.captionsOn ? this.captionsButton.dataset.labelOff : this.captionsButton.dataset.labelOn
          );
        }
      }
    }
  );
}
